import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import OutsideClickHandler from "react-outside-click-handler";
import { MdClose } from "react-icons/md";
import { HiOutlineEye } from "react-icons/hi";
import moment from "moment";
import { toast } from "react-toastify";
import { Oval } from "react-loader-spinner";
import { handleError } from "src/Common/function";
import { formatDate } from "src/Common/common";
import ReportService from "src/Services/API/report.service";
import CustomerService from "src/Services/API/customer.service";
import BranchService from "src/Services/API/branch.service";
import DateRangeSelector, { DATE_RANGE_OPTIONS } from "../components/DateRangeSelector";
import RecipientsChipInput from "../components/RecipientsChipInput";
import ReportGeneratedModal from "../components/ReportGeneratedModal";

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Sentinel selection for "All Customers" so the trigger/summary can show a label
// (distinct from the untouched/null state) while still resolving customerId to 0.
const ALL_CUSTOMERS = { id: 0, companyName: "All Customers" };

// Resolve the date-range UI selection into the flags the refrigerant endpoints expect.
const resolveDateFlags = (dateRange, customStart, customEnd) => {
  if (dateRange === "past7") {
    return { past7Days: true, past30Days: false, customDays: false, startDate: null, endDate: null };
  }
  if (dateRange === "past30") {
    return { past7Days: false, past30Days: true, customDays: false, startDate: null, endDate: null };
  }
  if (dateRange === "pastQuarter") {
    // No dedicated flag — express as a custom 90-day range.
    return {
      past7Days: false,
      past30Days: false,
      customDays: true,
      startDate: moment().subtract(90, "days").toDate(),
      endDate: new Date(),
    };
  }
  // custom
  return {
    past7Days: false,
    past30Days: false,
    customDays: true,
    startDate: customStart,
    endDate: customEnd,
  };
};

const ChevronIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 6L8 10L12 6" stroke="#868DA6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Track the on-screen rect of `anchorRef` while `open` so a portaled dropdown can
 * be positioned against it. The dropdowns are portaled to document.body to escape
 * the modal's `overflow-auto` container, which would otherwise clip them.
 */
const useAnchorRect = (anchorRef, open) => {
  const [rect, setRect] = useState(null);

  const update = useCallback(() => {
    if (!anchorRef.current) return;
    const bounds = anchorRef.current.getBoundingClientRect();
    const next = {
      top: Math.round(bounds.bottom + 4),
      left: Math.round(bounds.left),
      width: Math.round(bounds.width),
    };
    setRect((prev) =>
      prev && prev.top === next.top && prev.left === next.left && prev.width === next.width ? prev : next,
    );
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, update]);

  return rect;
};

/**
 * Searchable customer dropdown for the refrigerant report. Empty selection = "All Customers".
 */
const CustomerSelect = ({ selected, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const menuRect = useAnchorRect(anchorRef, open);

  useEffect(() => {
    if (!open) return undefined;
    const handle = (event) => {
      if (anchorRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  // Debounced search against the customer API while the dropdown is open.
  useEffect(() => {
    if (!open) return undefined;
    setLoading(true);
    const timer = setTimeout(() => {
      CustomerService.getAll(keyword)
        .then((response) => setCustomers(response.data || []))
        .catch((error) => handleError(error))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [keyword, open]);

  return (
    <div ref={anchorRef} className="relative w-full">
      <div
        onClick={() => setOpen((value) => !value)}
        className="w-full h-10 rounded-[6px] outline outline-1 -outline-offset-1 outline-[#E5E8F3] bg-white px-3 flex items-center justify-between gap-3 cursor-pointer"
      >
        <span
          className={`flex-1 font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] truncate ${
            selected ? "text-[#1A2332]" : "text-[#868DA6]"
          }`}
        >
          {selected ? selected.companyName : "Search for a customer..."}
        </span>
        {selected && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onSelect(null);
            }}
            className="text-[#868DA6] hover:text-[#1A2332]"
          >
            <MdClose size={16} />
          </button>
        )}
        <ChevronIcon />
      </div>

      {open &&
        menuRect &&
        createPortal(
          <div
            ref={menuRef}
            data-report-dropdown="true"
            className="fixed z-[60] max-h-[260px] overflow-y-auto py-1 bg-white rounded-[8px] border border-[#C9CEE2] shadow-[0px_4px_8px_rgba(47,43,67,0.10)]"
            style={{ top: menuRect.top, left: menuRect.left, width: menuRect.width }}
          >
            <div className="px-3 pt-1 pb-2 sticky top-0 bg-white">
              <input
                autoFocus
                className="w-full h-9 rounded-[6px] border border-[#E5E8F3] px-3 outline-none font-OpenSansRegular text-[14px] text-[#1A2332] placeholder:text-[#868DA6]"
                placeholder="Search customers..."
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
              />
            </div>

            <div
              onClick={() => {
                onSelect(ALL_CUSTOMERS);
                setOpen(false);
              }}
              className={`px-4 py-[10px] cursor-pointer hover:bg-[#EEF0F9] font-OpenSansSemiBold text-[14px] ${
                selected?.id === ALL_CUSTOMERS.id ? "text-[#0065FD]" : "text-[#5F6477]"
              }`}
            >
              All Customers
            </div>

            {loading ? (
              <div className="px-4 py-3 flex justify-center">
                <Oval visible height="18" width="18" color="#0065FD" strokeWidth={8} secondaryColor="#E5E8F3" />
              </div>
            ) : customers.length === 0 ? (
              <div className="px-4 py-[10px] font-OpenSansRegular text-[14px] text-[#868DA6]">
                No matching customers
              </div>
            ) : (
              customers.map((customer) => (
                <div
                  key={customer.id}
                  onClick={() => {
                    onSelect(customer);
                    setOpen(false);
                  }}
                  className="px-4 py-[10px] cursor-pointer hover:bg-[#EEF0F9] font-OpenSansRegular text-[14px] text-[#1A2332] truncate"
                >
                  {customer.companyName}
                </div>
              ))
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};

/**
 * Branch dropdown for the refrigerant report. Value 0 = "All Branches".
 */
const BranchSelect = ({ branches, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const menuRect = useAnchorRect(anchorRef, open);

  useEffect(() => {
    if (!open) return undefined;
    const handle = (event) => {
      if (anchorRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const selectedBranch = branches.find((branch) => branch.id === value);
  // value === null means nothing chosen yet (placeholder); 0 means "All Branches".
  const hasSelection = value != null;
  const label = !hasSelection
    ? "Select a branch..."
    : value === 0 || !selectedBranch
      ? "All Branches"
      : selectedBranch.name;

  return (
    <div ref={anchorRef} className="relative w-full">
      <div
        onClick={() => setOpen((value) => !value)}
        className="w-full h-10 rounded-[6px] outline outline-1 -outline-offset-1 outline-[#E5E8F3] bg-white px-3 flex items-center justify-between gap-3 cursor-pointer"
      >
        <span
          className={`flex-1 font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] truncate ${
            hasSelection ? "text-[#1A2332]" : "text-[#868DA6]"
          }`}
        >
          {label}
        </span>
        <ChevronIcon />
      </div>

      {open &&
        menuRect &&
        createPortal(
          <div
            ref={menuRef}
            data-report-dropdown="true"
            className="fixed z-[60] max-h-[240px] overflow-y-auto py-1 bg-white rounded-[8px] border border-[#C9CEE2] shadow-[0px_4px_8px_rgba(47,43,67,0.10)]"
            style={{ top: menuRect.top, left: menuRect.left, width: menuRect.width }}
          >
            {[{ id: 0, name: "All Branches" }, ...branches].map((branch) => (
              <div
                key={branch.id}
                onClick={() => {
                  onChange(branch.id);
                  setOpen(false);
                }}
                className={`px-4 py-[10px] cursor-pointer hover:bg-[#EEF0F9] font-OpenSansRegular text-[14px] truncate ${
                  branch.id === value ? "text-[#0065FD] font-OpenSansSemiBold" : "text-[#1A2332]"
                }`}
              >
                {branch.name}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

/**
 * Refrigerant Report modal: filters -> preview -> generate & email.
 *
 * shown / onClose: visibility control
 * userArray: enabled users in the branch (for resolving recipient chips)
 * branch: currently selected branch id (default for the Branch filter)
 */
const RefrigerantReportModal = ({ shown, onClose, userArray = [], branch }) => {
  const [step, setStep] = useState("filters"); // "filters" | "preview"
  const [customer, setCustomer] = useState(null);
  const [branchId, setBranchId] = useState(null);
  const [branches, setBranches] = useState([]);
  const [dateRange, setDateRange] = useState("past30");
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [recipients, setRecipients] = useState([]);

  const [preview, setPreview] = useState(null); // { recordsFound, rows }
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [generated, setGenerated] = useState(null); // { fileUrl, fileName }

  useEffect(() => {
    if (shown) {
      setStep("filters");
      setCustomer(null);
      setBranchId(null);
      setDateRange("past30");
      setCustomStart(null);
      setCustomEnd(null);
      setRecipients([]);
      setPreview(null);
      setIsPreviewing(false);
      setIsSending(false);
      setGenerated(null);
    }
  }, [shown, branch]);

  useEffect(() => {
    if (!shown) return;
    BranchService.getAll()
      .then((response) => setBranches(response.data || []))
      .catch((error) => handleError(error));
  }, [shown]);

  const selectableUsers = useMemo(() => userArray.filter((user) => user.email), [userArray]);

  const addRecipient = (email) => {
    if (!isValidEmail(email)) {
      toast("Please enter a valid email address");
      return;
    }
    if (recipients.some((recipient) => recipient.email?.toLowerCase() === email.toLowerCase())) return;
    const matchedUser = selectableUsers.find((user) => user.email?.toLowerCase() === email.toLowerCase());
    setRecipients((prev) => [
      ...prev,
      matchedUser
        ? { id: matchedUser.id, name: matchedUser.name, email: matchedUser.email, avatar: matchedUser.avatar }
        : { email },
    ]);
  };

  const toggleRecipient = (user) => {
    setRecipients((prev) => {
      const exists = prev.some((item) => (item.id ?? item.email) === (user.id ?? user.email));
      if (exists) return prev.filter((item) => (item.id ?? item.email) !== (user.id ?? user.email));
      return [...prev, { id: user.id, name: user.name, email: user.email, avatar: user.avatar }];
    });
  };

  const selectAllRecipients = (checked) => {
    setRecipients(
      checked
        ? selectableUsers.map((user) => ({ id: user.id, name: user.name, email: user.email, avatar: user.avatar }))
        : [],
    );
  };

  const removeRecipient = (recipient) => {
    setRecipients((prev) => prev.filter((item) => (item.id ?? item.email) !== (recipient.id ?? recipient.email)));
  };

  // Validate filters and resolve the API arguments shared by preview + send.
  const buildRequestArgs = () => {
    const flags = resolveDateFlags(dateRange, customStart, customEnd);
    if (flags.customDays && (!flags.startDate || !flags.endDate)) {
      toast("Please choose a start and end date");
      return null;
    }
    return {
      branchId,
      customerId: customer?.id ?? 0,
      past7Days: flags.past7Days,
      past30Days: flags.past30Days,
      customDays: flags.customDays,
      customStartDate: formatDate(flags.startDate, "MM/DD/YYYY"),
      customEndDate: formatDate(flags.endDate, "MM/DD/YYYY"),
    };
  };

  const loadPreview = () => {
    const args = buildRequestArgs();
    if (!args) return;
    setIsPreviewing(true);
    ReportService.getRefrigerantReportPreview(
      args.branchId,
      args.customerId,
      args.past7Days,
      args.past30Days,
      args.customDays,
      args.customStartDate,
      args.customEndDate,
    )
      .then((response) => {
        setPreview(response.data || { recordsFound: 0, rows: [] });
        setStep("preview");
      })
      .catch((error) => handleError(error))
      .finally(() => setIsPreviewing(false));
  };

  const sendReport = () => {
    const recipientIds = recipients
      .filter((recipient) => recipient.id != null)
      .map((recipient) => String(recipient.id));
    if (recipientIds.length === 0) {
      toast("Please select at least one recipient that is a user in this branch");
      return;
    }
    const args = buildRequestArgs();
    if (!args) return;
    setIsSending(true);
    ReportService.sendRefrigerantReport(
      args.branchId,
      args.customerId,
      recipientIds,
      args.past7Days,
      args.past30Days,
      args.customDays,
      args.customStartDate,
      args.customEndDate,
    )
      .then((response) => {
        toast("Your report has been sent successfully");
        const data = response?.data || {};
        setGenerated({ fileUrl: data.fileUrl, fileName: data.fileName, fileSize: data.fileSize });
      })
      .catch((error) => handleError(error))
      .finally(() => setIsSending(false));
  };

  const handleSuccessClose = () => {
    setGenerated(null);
    onClose();
  };

  if (!shown) return null;

  if (generated) {
    return (
      <ReportGeneratedModal
        shown
        onClose={handleSuccessClose}
        fileUrl={generated.fileUrl}
        fileName={generated.fileName || "Refrigerant_Usage_Report.xlsx"}
        fileSize={generated.fileSize}
      />
    );
  }

  // Display label for the selected date range (used in preview summary).
  const dateRangeLabel = (() => {
    if (dateRange === "custom") {
      return customStart && customEnd
        ? `${moment(customStart).format("MMM D")} - ${moment(customEnd).format("MMM D, YYYY")}`
        : "Custom Range";
    }
    const option = DATE_RANGE_OPTIONS.find((item) => item.key === dateRange);
    const end = moment();
    const start = moment().subtract(option?.days || 30, "days");
    return `${option?.label} (${start.format("MMM D")} - ${end.format("MMM D")})`;
  })();

  const selectedBranchLabel =
    branchId === 0 ? "All Branches" : branches.find((item) => item.id === branchId)?.name || "—";

  const previewRows = preview?.rows?.slice(0, 5) ?? [];

  // Preview/Send require an explicit customer and branch selection.
  const filtersIncomplete = !customer || branchId == null;

  return (
    <div className="fixed w-screen h-screen left-0 top-0 bg-black bg-opacity-30 flex items-center justify-center z-30">
      <OutsideClickHandler
        onOutsideClick={(event) => {
          // Branch/Customer/Recipients dropdowns are portaled to document.body, so
          // clicks inside them count as "outside" this modal. Ignore those.
          if (event.target.closest?.("[data-report-dropdown],[data-recipients-dropdown]")) return;
          onClose();
        }}
      >
        <div
          className={`${
            step === "preview" ? "w-[1000px]" : "w-[640px]"
          } max-h-[90vh] overflow-auto bg-white rounded-[12px] shadow-[0px_10px_10px_-5px_rgba(0,0,0,0.04),0px_20px_25px_-5px_rgba(0,0,0,0.10)]`}
        >
          {/* Header */}
          <div
            className={`flex items-start gap-3 border-b-[1px] border-[#E5E8F3] ${
              step === "preview" ? "px-8 py-6" : "px-6 py-4"
            }`}
          >
            <div className="flex-1 flex flex-col gap-1">
              <p
                className={`font-OpenSansSemiBold capitalize text-[#1A2332] ${
                  step === "preview" ? "text-[24px] leading-[32px]" : "text-[20px] leading-[28px]"
                }`}
              >
                {step === "filters" ? "Generate Refrigerant Report" : "Refrigerant Report Preview"}
              </p>
              <p
                className={`font-OpenSansRegular tracking-[0.14px] text-[#7C829A] ${
                  step === "preview" ? "text-[16px] leading-[24px]" : "text-[14px] leading-[20px]"
                }`}
              >
                {step === "filters"
                  ? "Configure filters and export refrigerant usage data."
                  : "Review the report before sending it to recipients."}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-[#868DA6] hover:text-[#1A2332]"
            >
              <MdClose size={18} />
            </button>
          </div>

          {step === "filters" ? (
            <>
              {/* Filters body */}
              <div className="p-8 flex flex-col gap-8">
                <div className="flex flex-col gap-1">
                  <p className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#1A2332]">
                    Customer
                  </p>
                  <CustomerSelect selected={customer} onSelect={setCustomer} />
                </div>

                <div className="flex flex-col gap-3">
                  <p className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#1A2332]">
                    Date Range
                  </p>
                  <DateRangeSelector
                    selected={dateRange}
                    onSelect={setDateRange}
                    customStart={customStart}
                    customEnd={customEnd}
                    onCustomChange={([start, end]) => {
                      setCustomStart(start);
                      setCustomEnd(end);
                    }}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <p className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#1A2332]">
                    Branch
                  </p>
                  <BranchSelect branches={branches} value={branchId} onChange={setBranchId} />
                </div>

                <div className="flex flex-col gap-2">
                  <p className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#1A2332]">
                    Recipients
                  </p>
                  <RecipientsChipInput
                    recipients={recipients}
                    users={selectableUsers}
                    onAdd={addRecipient}
                    onToggle={toggleRecipient}
                    onSelectAll={selectAllRecipients}
                    onRemove={removeRecipient}
                  />
                </div>
              </div>

              {/* Filters footer */}
              <div className="flex items-center justify-end gap-4 px-6 py-4 border-t-[1px] border-[#E5E8F3]">
                <button
                  type="button"
                  onClick={onClose}
                  className="h-10 px-1 rounded-[6px] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#7C829A]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={loadPreview}
                  disabled={isPreviewing || filtersIncomplete}
                  className={`h-10 px-4 rounded-[8px] bg-white outline outline-1 -outline-offset-1 outline-[#C9CEE2] shadow-[0px_1px_2px_rgba(16,24,40,0.05)] flex items-center justify-center gap-2 min-w-[120px] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#1A2332] ${
                    isPreviewing || filtersIncomplete ? "cursor-not-allowed opacity-80" : "hover:bg-[#F7F9FC]"
                  }`}
                >
                  {isPreviewing ? (
                    <Oval visible height="18" width="18" color="#0065FD" strokeWidth={8} secondaryColor="#E5E8F3" />
                  ) : (
                    <>
                      <HiOutlineEye size={16} />
                      Preview
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={sendReport}
                  disabled={isSending || filtersIncomplete}
                  className={`h-10 px-4 rounded-[8px] bg-[#0065FD] flex items-center justify-center min-w-[130px] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-white ${
                    isSending || filtersIncomplete ? "cursor-not-allowed opacity-80" : "hover:bg-[#0058E5]"
                  }`}
                >
                  {isSending ? (
                    <Oval visible height="18" width="18" color="#FFFFFF" strokeWidth={8} secondaryColor="#FFEEFF" />
                  ) : (
                    "Send Report"
                  )}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Preview body */}
              <div className="p-8 flex flex-col gap-8">
                <div className="rounded-[10px] bg-[#F8F9FE] outline outline-1 -outline-offset-1 outline-[#E5E8F3] p-6 flex flex-col gap-4">
                  <p className="font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#101828]">
                    Report Summary
                  </p>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                    <SummaryField label="Branches" value={selectedBranchLabel} />
                    <SummaryField label="Date Range" value={dateRangeLabel} />
                    <SummaryField label="Customer" value={customer ? customer.companyName : "All Customers"} />
                    <SummaryField label="Records Found" value={String(preview?.recordsFound ?? 0)} />
                  </div>
                  <div className="border-t-[1px] border-[#E5E8F3] pt-4 flex flex-col gap-2">
                    <p className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] uppercase text-[#7C829A]">
                      Recipients
                    </p>
                    <div className="flex flex-wrap content-center items-center gap-2">
                      {recipients.length === 0 ? (
                        <span className="font-OpenSansRegular text-[14px] text-[#868DA6]">No recipients selected</span>
                      ) : (
                        recipients.map((recipient, index) => (
                          <span
                            key={recipient.id ?? recipient.email ?? index}
                            className="flex items-center gap-1 pl-3 pr-2.5 py-1 rounded-[16px] bg-[#FBFCFF] outline outline-1 -outline-offset-1 outline-[#E5E8F3]"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="none"
                            >
                              <path
                                d="M10.7983 3.18359C10.973 3.07235 11.2051 3.12373 11.3164 3.29834C11.4277 3.47299 11.3763 3.70512 11.2017 3.81641L6.70605 6.67969C6.70174 6.68244 6.6973 6.68542 6.69287 6.68799C6.48318 6.80976 6.24492 6.8735 6.00244 6.87354C5.75987 6.87354 5.52128 6.80982 5.31152 6.68799C5.30715 6.68545 5.30309 6.6824 5.29883 6.67969L0.798828 3.81641C0.624138 3.70524 0.572507 3.47354 0.683594 3.29883C0.794755 3.12414 1.02646 3.07251 1.20117 3.18359L5.68799 6.03906C5.78333 6.09444 5.89218 6.12354 6.00244 6.12354C6.1126 6.1235 6.22065 6.09437 6.31592 6.03906L10.7983 3.18359Z"
                                fill="#1A2332"
                              />
                              <path
                                d="M10.625 3C10.625 2.65482 10.3452 2.375 10 2.375H2C1.65482 2.375 1.375 2.65482 1.375 3V9C1.375 9.34518 1.65482 9.625 2 9.625H10C10.3452 9.625 10.625 9.34518 10.625 9V3ZM11.375 9C11.375 9.75939 10.7594 10.375 10 10.375H2C1.24061 10.375 0.625 9.75939 0.625 9V3C0.625 2.24061 1.24061 1.625 2 1.625H10C10.7594 1.625 11.375 2.24061 11.375 3V9Z"
                                fill="#1A2332"
                              />
                            </svg>
                            <span className="text-center font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332]">
                              {recipient.name && recipient.email
                                ? `${recipient.name} (${recipient.email})`
                                : recipient.email || recipient.name}
                            </span>
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  {/* Records table */}
                  <div className="rounded-[12px] bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.06)] outline outline-1 -outline-offset-1 outline-[#E5E8F3] overflow-hidden">
                    {previewRows.length ? (
                      <div className="flex items-start">
                        <PreviewColumn
                          header="Date"
                          rows={previewRows}
                          className="flex-1"
                          render={(row) => (
                            <span className="font-OpenSansSemiBold text-[14px] leading-[20px] text-[#1A2332]">
                              {row.date}
                            </span>
                          )}
                        />
                        <PreviewColumn
                          header="WO #"
                          rows={previewRows}
                          className="w-[200px]"
                          render={(row) => (
                            <span className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477]">
                              {row.woNumber}
                            </span>
                          )}
                        />
                        <PreviewColumn
                          header="Customer"
                          rows={previewRows}
                          className="w-[200px]"
                          render={(row) => (
                            <span className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477] truncate">
                              {row.customer}
                            </span>
                          )}
                        />
                        <PreviewColumn
                          header="Refrigerant Type"
                          rows={previewRows}
                          className="w-[200px]"
                          render={(row) => (
                            <span className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477]">
                              {row.refrigerantType}
                            </span>
                          )}
                        />
                        <PreviewColumn
                          header="Amount"
                          rows={previewRows}
                          className="w-[200px]"
                          render={(row) => (
                            <span className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477]">
                              {row.amount}
                            </span>
                          )}
                        />
                      </div>
                    ) : (
                      <div className="px-5 py-8 text-center font-OpenSansRegular text-[14px] text-[#868DA6]">
                        No refrigerant usage found for the selected filters.
                      </div>
                    )}
                  </div>
                  <p className="font-OpenSansRegular italic text-[14px] leading-[20px] text-[#7C829A]">
                    Preview only. Showing sample report data.
                  </p>
                </div>
              </div>

              {/* Preview footer */}
              <div className="flex items-center justify-end gap-4 px-6 py-4 border-t-[1px] border-[#E5E8F3]">
                <button
                  type="button"
                  onClick={() => setStep("filters")}
                  className="h-10 px-4 rounded-[8px] bg-white outline outline-1 -outline-offset-1 outline-[#C9CEE2] shadow-[0px_1px_2px_rgba(16,24,40,0.05)] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#1A2332] hover:bg-[#F7F9FC]"
                >
                  Back To Filters
                </button>
                <button
                  type="button"
                  onClick={sendReport}
                  disabled={isSending || filtersIncomplete}
                  className={`h-10 px-4 rounded-[8px] bg-[#0065FD] flex items-center justify-center min-w-[130px] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-white ${
                    isSending || filtersIncomplete ? "cursor-not-allowed opacity-80" : "hover:bg-[#0058E5]"
                  }`}
                >
                  {isSending ? (
                    <Oval visible height="18" width="18" color="#FFFFFF" strokeWidth={8} secondaryColor="#FFEEFF" />
                  ) : (
                    "Send Report"
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </OutsideClickHandler>
    </div>
  );
};

const MailIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1.5 3.25C1.5 2.83579 1.83579 2.5 2.25 2.5H9.75C10.1642 2.5 10.5 2.83579 10.5 3.25V8.75C10.5 9.16421 10.1642 9.5 9.75 9.5H2.25C1.83579 9.5 1.5 9.16421 1.5 8.75V3.25Z"
      stroke="#1A2332"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1.75 3.25L6 6.5L10.25 3.25"
      stroke="#1A2332"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SummaryField = ({ label, value }) => (
  <div className="flex flex-col gap-1 min-w-0">
    <p className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] uppercase text-[#7C829A]">
      {label}
    </p>
    <p className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#1A2332]">
      {value}
    </p>
  </div>
);

// A single column of the preview table: a sticky-looking header cell followed by
// one cell per row. Rendering column-by-column (rather than row-by-row) keeps each
// column a fixed width and lets long Customer values truncate independently.
const PreviewColumn = ({ header, rows, render, className = "" }) => (
  <div className={`flex flex-col ${className}`}>
    <div className="h-11 px-4 py-3 bg-[#F8F9FE] border-b-[1px] border-[#E5E8F3] flex items-center">
      <span className="font-OpenSansSemiBold text-[12px] leading-[18px] text-[#5F6477]">{header}</span>
    </div>
    {rows.map((row, index) => (
      <div
        key={index}
        className="px-4 py-3 border-b-[1px] border-[#E5E8F3] last:border-b-0 flex items-center overflow-hidden"
      >
        {render(row)}
      </div>
    ))}
  </div>
);

export default RefrigerantReportModal;
